import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import LoadingSpinner from 'components/LoadingSpinner';
import 'react-table/react-table.css';
import {
  getAccountStoreFields,
  getUserAccounts,
  getOrCreateAccounts,
  unClaimAccount,
  inviteUser,
} from 'utils/api.service';
import { setLightboxState } from 'store/actions/lightbox_actions';
import ReactTooltip from 'react-tooltip';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { addNewNotification } from 'store/actions/notification_actions';
import ConfirmationModal from 'components/ConfirmationModal';

const AccountsStore = () => {
  const [loadingMasterData, setLoadingMasterData] = useState(true);
  const [loadingDataStore, setLoadingDataStore] = useState(false);
  const [data, setData] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedAccountType, setAccountType] = useState('');
  const [selectedCountryCode, setCountryCode] = useState('');
  const [masterData, setMasterData] = useState(null);
  const [hasSearchError, setHasSearchError] = useState(false);
  const [claimingDataStore, setClaimingDataStore] = useState(false);
  const [claimList, setClaimList] = useState([]);
  const [dataWasClaimed, setDataWasClaimed] = useState(false);
  const [dataWasDiscarded, setDataWasDiscarded] = useState(false);
  const [dataWasSearched, setDataWasSearched] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState('');
  const [inviteStatus, setInviteStatus] = useState({});

  const dispatch = useDispatch();

  const fetchUserAccounts = async () => {
    try {
      const { data } = await getUserAccounts();
      setClaimList(data);
    } catch (error) {
      dispatch(addNewNotification('Error fetching user accounts', 'danger'));
    }
  };

  useEffect(() => {
    const fetchAccountStoreFields = async () => {
      try {
        const {
          data: { environments, accountTypes, countries },
        } = await getAccountStoreFields();
        setMasterData({ environments, accountTypes, countries });
        setSelectedEnvironment(environments[0]);
        setAccountType(accountTypes[0]);
        setCountryCode('US');
      } catch (error) {
        dispatch(addNewNotification('Error fetching store fields', 'danger'));
      }
    };

    const fetchData = async () => {
      await Promise.all([fetchAccountStoreFields(), fetchUserAccounts()]);
      setLoadingMasterData(false);
    };

    fetchData();
  }, []);

  const searchDataStore = async () => {
    const getDataStores = async () => {
      setLoadingDataStore(true);
      setData(null);
      setHasSearchError(false);
      try {
        const data = await getOrCreateAccounts({
          accountType: selectedAccountType,
          env: selectedEnvironment,
          countryCode: selectedCountryCode,
        });
        setData(data.data);
        setDataWasSearched(true);
        setDataWasDiscarded(false);
        setLoadingDataStore(false);
        setDataWasClaimed(false);
      } catch (error) {
        setHasSearchError(true);
        setSearchErrorMessage(
          error?.response?.data || 'Error fetching data store'
        );
        setLoadingDataStore(false);
        setDataWasSearched(false);
      }
    };
    Promise.all([getDataStores(), fetchUserAccounts()]);
  };

  const showConfirmationModal = () => {
    setIsModalOpen(true);
  };

  const onUnClaimAccountConfirmation = async () => {
    setIsModalOpen(false);
    dispatch(setLightboxState({ visible: true }));
    const payload = {
      accountType: data.accountType,
      csn: data.csn,
    };
    try {
      await unClaimAccount(payload);
      dispatch(
        addNewNotification(
          'This data was successfully sent back to the data pool',
          'success'
        )
      );
      setDataWasDiscarded(true);
    } catch ({
      response: {
        data: { detail: errorDetail },
      },
    }) {
      dispatch(addNewNotification(errorDetail, 'danger'));
    } finally {
      setClaimingDataStore(false);
      dispatch(setLightboxState({ visible: false }));
      setDataWasSearched(false);
    }
  };

  const handleEnvironmentChange = (event) => {
    setSelectedEnvironment(event.target.value);
  };

  const handleAccountTypeChange = (event) => {
    setAccountType(event.target.value);
  };

  const handleCountryCodeChange = (event) => {
    setCountryCode(event.target.value);
  };

  const handleInviteUser = async (rowData) => {
    const csn = rowData.csn;
    setInviteStatus((prev) => ({
      ...prev,
      [csn]: { loading: true, message: '', type: '' },
    }));
    try {
      const payload = {
        accountType: rowData.accountType,
        csn: rowData.csn,
      };
      const response = await inviteUser(payload);
      if (response.data.invitationUrl) {
        window.open(response.data.invitationUrl, '_blank');
        setInviteStatus((prev) => ({
          ...prev,
          [csn]: {
            loading: false,
            message:
              'Invite sent successfully!, Please switch to newly open tab and complete the registration',
            type: 'success',
          },
        }));
      } else {
        setInviteStatus((prev) => ({
          ...prev,
          [csn]: {
            loading: false,
            registeredToOxygen: response.data.registeredToOxygen || false,
            message:
              'Invite failed: ' +
              JSON.stringify(response.data.error || { error: 'Unknown error' }),
            type: 'error',
          },
        }));
      }
    } catch (error) {
      setInviteStatus((prev) => ({
        ...prev,
        [csn]: {
          loading: false,
          message:
            'API error: ' +
            (error?.response?.data || error.message || 'Unknown error'),
          type: 'error',
        },
      }));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <h2 className="title">Accounts and Contacts</h2>
        <a
          href="https://wiki.autodesk.com/pages/viewpage.action?pageId=2788901089"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '12px',
            color: '#007bff',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            marginTop: '8px',
          }}
          title="Help Documentation"
        >
          <span role="img" aria-label="question mark">
            ❓
          </span>{' '}
          Help
        </a>
      </div>
      <hr />
      {loadingMasterData ? (
        <LoadingSpinner text="Loading master data" />
      ) : (
        <div>
          <div className="search-header">
            <section>
              <label htmlFor="environmet">Environment</label>
              <select
                className="form-control"
                name="environment"
                value={selectedEnvironment}
                onChange={handleEnvironmentChange}
              >
                {masterData.environments.map((environment) => (
                  <option key={environment} value={environment}>
                    {environment}
                  </option>
                ))}
              </select>
            </section>
            <section>
              <label htmlFor="accounttype">AccountType</label>
              <select
                className="form-control"
                id="accounttype"
                value={selectedAccountType}
                onChange={handleAccountTypeChange}
              >
                {masterData.accountTypes.map((at) => (
                  <option key={at} value={at}>
                    {at}
                  </option>
                ))}
              </select>
            </section>
            <section>
              <label htmlFor="countrycode">County</label>
              <select
                className="form-control"
                id="countrycode"
                value={selectedCountryCode}
                onChange={handleCountryCodeChange}
              >
                {masterData.countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </section>

            {dataWasSearched && (
              <ReactTooltip
                id="search-button"
                place="top"
                effect="solid"
                className="tooltip"
              >
                Search result has to be claimed or discarded to trigger a new
                search
              </ReactTooltip>
            )}
            <button
              className="btn btn-primary btn-search"
              onClick={searchDataStore}
              disabled={
                loadingDataStore || claimingDataStore || dataWasSearched
              }
              data-tip
              data-for="search-button"
            >
              Search
            </button>
          </div>
          <div className="spacer" />
          {loadingDataStore ? (
            <LoadingSpinner text="Loading search data" />
          ) : (
            data &&
            !dataWasDiscarded && (
              <div>
                <div>
                  <h4 className="title">Found 1 result </h4>
                </div>
                <div className="table-container">
                  <ReactTable
                    data={
                      (console.log(data) || true) && [
                        {
                          ...data,
                          date: data['created_date'].split(' ')[0],
                        },
                      ]
                    }
                    columns={[
                      {
                        Header: 'Date',
                        accessor: 'date',
                        width: 200,
                        className: 'text-center text-center_vertical',
                      },
                      {
                        Header: 'Env',
                        accessor: 'env',
                        className: 'text-center text-center_vertical',
                        width: 150,
                      },
                      {
                        Header: 'Test Data',
                        accessor: 'details',
                        className: 'text-center_vertical',
                        Cell: ({ original }) =>
                          Object.entries(original).map(([key, value]) => {
                            if (
                              key === 'message' &&
                              typeof value === 'string'
                            ) {
                              if (
                                value
                                  .trim()
                                  .toLowerCase()
                                  .startsWith('warning:')
                              ) {
                                return (
                                  <div key={key}>
                                    <div>
                                      <code>
                                        {key}
                                        {': '}
                                      </code>
                                      <div className="warning-box">
                                        {value.trim()}
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else if (
                                value
                                  .trim()
                                  .toLowerCase()
                                  .startsWith('success:')
                              ) {
                                return (
                                  <div key={key}>
                                    <div>
                                      <code>
                                        {key}
                                        {': '}
                                      </code>
                                      <div className="success-box">
                                        {value.trim()}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                            }
                            return (
                              <div key={key}>
                                <div>
                                  <code>
                                    {key}
                                    {': '}
                                  </code>
                                  <samp>
                                    {typeof value === 'boolean'
                                      ? value.toString()
                                      : value}
                                  </samp>
                                </div>
                              </div>
                            );
                          }),
                      },
                      {
                        Header: '',
                        accessor: 'claim',
                        width: 100,
                        align: 'center',
                        className: 'text-center center_vertical',
                        Cell: (_) => {
                          const disabledTooltip =
                            'Test data was already claimed You need to trigger a new search';
                          return (
                            <div className="button-container">
                              <ReactTooltip
                                id="claim-button"
                                place="left"
                                effect="solid"
                              >
                                {dataWasClaimed
                                  ? disabledTooltip
                                  : 'Claim action will confirm this test data for you'}
                              </ReactTooltip>
                              <button
                                className="btn btn-primary btn-search btn-column"
                                onClick={() => {
                                  dispatch(
                                    addNewNotification(
                                      'Claimed data store was confirmed',
                                      'success'
                                    )
                                  );
                                  setDataWasClaimed(true);
                                  setDataWasSearched(false);
                                }}
                                disabled={claimingDataStore || dataWasClaimed}
                                data-tip
                                data-for="claim-button"
                              >
                                {dataWasClaimed ? 'Claimed' : 'Claim'}
                              </button>
                              <div className="spacer" />
                              <div className="button-container">
                                <ReactTooltip
                                  id="discard-button"
                                  place="left"
                                  effect="solid"
                                >
                                  By default this data is reserved for you. This
                                  action will send the data back to data pool
                                </ReactTooltip>
                                <button
                                  className="btn btn-primary btn-search btn-column"
                                  onClick={showConfirmationModal}
                                  data-tip
                                  data-for="discard-button"
                                >
                                  Discard
                                </button>
                              </div>
                            </div>
                          );
                        },
                      },
                    ]}
                    defaultPageSize={1}
                    loading={loadingDataStore}
                    page={0}
                    showPagination={false}
                    sortable={false}
                  />
                </div>
              </div>
            )
          )}
          {hasSearchError && (
            <h4 className="error-message title no-data">
              {searchErrorMessage}
            </h4>
          )}
          <div className="spacer" />
          {claimList.length > 0 && (
            <>
              <h4 className="title">Your last claimed accounts</h4>
              <ReactTable
                defaultSorted={[
                  {
                    id: 'claimeddate',
                    desc: true,
                  },
                ]}
                data={claimList}
                columns={[
                  {
                    Header: 'Claimed On',
                    accessor: 'claimeddate',
                    className: 'text-center',
                    width: 150,
                    Cell: ({ original }) =>
                      original.claimeddate
                        ? original.claimeddate.split(' ')[0]
                        : '',
                  },
                  {
                    Header: 'Env',
                    accessor: 'env',
                    className: 'text-center',
                    width: 50,
                  },
                  {
                    Header: 'CountryCode',
                    accessor: 'country',
                    className: 'text-center',
                    width: 100,
                  },
                  {
                    Header: 'CSN',
                    accessor: 'csn',
                    className: 'text-center',
                    width: 200,
                  },

                  {
                    Header: 'Test Data',
                    accessor: 'details',
                    className: 'text-center_vertical',
                    Cell: ({ original }) => (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          justifyContent: 'flex-end',
                        }}
                      >
                        {Object.entries(original).map(([key, value]) => {
                          if (key === 'inviteUserStatus') return null; // Skip rendering here
                          if (key === 'message' && typeof value === 'string') {
                            if (
                              value.trim().toLowerCase().startsWith('warning:')
                            ) {
                              return (
                                <div key={key}>
                                  <div>
                                    <code>
                                      {key}
                                      {': '}
                                    </code>
                                    <div className="warning-box">
                                      {value.trim()}
                                    </div>
                                  </div>
                                </div>
                              );
                            } else if (
                              value.trim().toLowerCase().startsWith('success:')
                            ) {
                              return (
                                <div key={key}>
                                  <div>
                                    <code>
                                      {key}
                                      {': '}
                                    </code>
                                    <div className="success-box">
                                      {value.trim()}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          }
                          return (
                            <div key={key}>
                              <div>
                                <code>
                                  {key}
                                  {': '}
                                </code>
                                <samp>
                                  {typeof value === 'boolean'
                                    ? value.toString()
                                    : value}
                                </samp>
                              </div>
                            </div>
                          );
                        })}
                        {!original.registeredToOxygen && (
                          <div
                            style={{
                              marginTop: '16px',
                              padding: '12px',
                              borderRadius: '8px',
                            }}
                          >
                            <button
                              className="btn btn-primary btn-invite-user"
                              style={{ minWidth: 120, position: 'relative' }}
                              onClick={() => handleInviteUser(original)}
                              disabled={
                                inviteStatus[original.csn]?.loading ||
                                inviteStatus[original.csn]?.registeredToOxygen
                              }
                            >
                              {inviteStatus[original.csn]?.loading ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                  style={{ marginRight: 8 }}
                                ></span>
                              ) : null}
                              Register this User
                            </button>
                            {inviteStatus[original.csn]?.message && (
                              <span
                                className={`invite-status-message${
                                  inviteStatus[original.csn].type
                                    ? ` ${inviteStatus[original.csn].type}`
                                    : ''
                                }`}
                              >
                                {inviteStatus[original.csn].message}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ),
                  },
                ]}
                defaultPageSize={10}
                className="-striped -highlight"
                loading={loadingDataStore}
              />
            </>
          )}
          {isModalOpen && (
            <ConfirmationModal
              title="Discard Data Store"
              message="Are you sure you want to discard this data store?"
              onClose={() => setIsModalOpen(false)}
              onConfirm={onUnClaimAccountConfirmation}
            >
              <p>
                By discarding this data store, you are sending it back to the
                data pool. This action cannot be undone.
              </p>
            </ConfirmationModal>
          )}
        </div>
      )}
    </div>
  );
};
export default AccountsStore;
